import { useSnackbar } from "notistack";
import { Avatar, Box, Typography, Link, Grid } from "@mui/material";
import styled from "@emotion/styled";
import { useTranslation } from "react-i18next";
import React, { useCallback, useEffect, useRef, useState } from "react";
import AgGrid from "../../../components/Table/AgGrid";
import CustomizedBreadcrumbs from "../../../components/Custom/CustomizedBreadcrumbs";
import CustomizedLetterAvatar from "../../../components/Custom/CustomizedLetterAvatar";
import { entryTypeToThai } from "../../../utils/dataTransformer";
import CustomizedButton from "../../../components/Custom/CustomizedButton";
import InventoryService from "../../../services/Inventory";
import {
  dateToUnix,
  mapStatusToThai,
  unixToDateWithFormat,
} from "../../../utils/date-converter";
import CustomizedStatus from "../../../components/Custom/CustomizedStatus";
import NewReportDateFilter from "../../../components/UI/newReportDateFilter";
import moment from "moment";
import { useForm } from "react-hook-form";
import {
  exportCSVParams,
  filterParamsOptions,
} from "../../../utils/filterparams";
// import { unixToDateWithFormat } from "../../../utils/date-converter";

const StyledLink = styled(Link)(({ theme }) => ({
  color: theme.palette.primary.main,
  textDecoration: "none",
  cursor: "pointer",
}));

const NotActiveSerialNumber = () => {
  const gridRef = useRef();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [isLoadingExport, setIsLoadingExport] = useState(false);
  const [allItems, setAllItems] = useState([]);
  const [isLoadingFilter, setIsLoadingFilter] = useState(false);

  const navigateToGoodPage = (type, document_id) => {
    window.open(
      `/inventory/${type}/${encodeURIComponent(document_id)}`,
      "_blank"
    );
  };

  const breadcrumbs = [
    {
      name: t("inventory.index"),
      to: "/inventory",
    },
    {
      name: t("inventory.report"),
      to: "/inventory/report",
    },
    {
      name: t("inventory.notActiveSerialNumber.index"),
    },
  ];

  const columnDefs = [
    {
      field: "created_date",
      headerName: t("inventory.stockEntry.createdDate"),
      filter: false,
    },
    {
      field: "serial_number",
      headerName: t("inventory.serialNumber"),
      filter: "agTextColumnFilter",
      filterParams: {
        filterOptions: filterParamsOptions("string"),
      },
    },
    {
      field: "item_group_sub_level_1_name",
      headerName: t("inventory.items.itemGroup"),
      filter: "agTextColumnFilter",
      filterParams: {
        filterOptions: filterParamsOptions("string"),
      },
    },
    {
      field: "item_document_id",
      headerName: t("inventory.items.itemId"),
      filter: "agTextColumnFilter",
      filterParams: {
        filterOptions: filterParamsOptions("string"),
      },
    },
    {
      field: "item_name",
      headerName: t("inventory.items.itemName"),
      filter: "agTextColumnFilter",
      filterParams: {
        filterOptions: filterParamsOptions("string"),
      },
    },
    {
      field: "item_description",
      headerName: t("inventory.items.itemDescription"),
      filter: "agTextColumnFilter",
      filterParams: {
        filterOptions: filterParamsOptions("string"),
      },
    },
    {
      field: "posted_quantity",
      headerName: t("inventory.list.quantityDifference"),
      filter: "agNumberColumnFilter",
      filterParams: {
        filterOptions: filterParamsOptions("number"),
      },
    },
    {
      field: "uom_name",
      headerName: t("inventory.unit"),
      filter: "agTextColumnFilter",
      filterParams: {
        filterOptions: filterParamsOptions("string"),
      },
    },
    {
      field: "reference_document_type_thai_name",
      headerName: t("inventory.stockEntry.entryDocumentType"),
      filter: "agSetColumnFilter",
      filterParams: {
        values: ["นำเข้า", "นำออก", "โอนย้าย", "ปรับปรุง"],
      },
    },
    {
      field: "source_warehouse_thai_name",
      headerName: t("inventory.list.sourceWarehouse"),
      filter: "agTextColumnFilter",
      filterParams: {
        filterOptions: filterParamsOptions("string"),
      },
    },
    {
      field: "batch_number",
      headerName: "Barcode",
      filter: "agTextColumnFilter",
      filterParams: {
        filterOptions: filterParamsOptions("string"),
      },
    },
    {
      field: "reference_document_id",
      headerName: t("inventory.stockEntry.referenceDocument"),
      filter: "agTextColumnFilter",
      filterParams: {
        filterOptions: filterParamsOptions("string"),
      },
      cellRenderer: (params) => {
        const { reference_document_type, reference_document_id } = params.data;
        if (!reference_document_type || !reference_document_id) {
          return "-";
        }
        return (
          <StyledLink
            onClick={() =>
              navigateToGoodPage(reference_document_type, reference_document_id)
            }
          >
            {reference_document_id}
          </StyledLink>
        );
      },
    },
    {
      field: "status",
      headerName: t("inventory.status") + "เอกสาร",
      filter: "agSetColumnFilter",
      filterParams: {
        values: ["ร่าง", "เสร็จสิ้น", "ยกเลิก"],
      },
      cellRenderer: (params) => {
        return <CustomizedStatus status={params.value} />;
      },
      cellStyle: {
        display: "flex",
        alignItems: "center",
      },
    },
    {
      field: "created_by_full_name",
      headerName: t("inventory.stockEntry.createdBy"),
      sortable: false,
      filter: "agSetColumnFilter",
      enablePivot: true,
      cellRenderer: (params) => {
        if (params.data.created_by.img_url) {
          return <Avatar alt="img_url" src={params.data.created_by.img_url} />;
        } else {
          return (
            <CustomizedLetterAvatar
              name={
                params.data.created_by.first_name +
                " " +
                params.data.created_by.last_name
              }
            />
          );
        }
      },
    },
  ];

  const formatValueForAgGrid = () => {
    if (allItems.length !== 0) {
      const formatValue = allItems.map((te, index) => {
        let status = false;
        if (te.stock_entry.inventory_object_item) {
          const notNull = te.stock_entry.inventory_object_item;
          if (notNull.goods_receive) status = notNull.goods_receive.status;
          else if (notNull.goods_issue) status = notNull.goods_issue.status;
          else if (notNull.goods_transfer)
            status = notNull.goods_transfer.status;
          else status = notNull.goods_adjustment.status;
        }
        return {
          ...te,
          item_document_id: te.item_document_id,
          item_name: te.item?.name,
          item_description: te.item?.description,
          item_group_sub_level_1_name: te.item?.item_group_sub_level_1?.name,
          status: mapStatusToThai(status),
          uom_name: te.uom?.name,
          reference_document_type_thai_name: entryTypeToThai(
            te.stock_entry?.reference_document_type
          ),
          source_warehouse_document_id:
            te.stock_entry?.source_warehouse?.document_id,
          source_warehouse_thai_name:
            te.stock_entry?.source_warehouse?.thai_name,
          reference_document_type: te.stock_entry?.reference_document_type,
          reference_document_id: te.stock_entry?.reference_document_id,
          created_by_full_name: te.created_by
            ? te.created_by?.first_name + " " + te.created_by?.last_name
            : "",
          created_date: unixToDateWithFormat(te.created_date),
        };
      });
      return formatValue;
    } else return [];
  };

  const { control, getValues, reset, setValue } = useForm({
    defaultValues: {
      dateType: "lastWeek",
      date: moment(new Date()).startOf("day").subtract(7, "day"),
      dateTo: new Date(),
    },
  });

  const exportHandler = async () => {
    try {
      let startDate = getValues("date");
      let endDate = getValues("dateTo");

      const params = exportCSVParams(
        t("inventory.notActiveSerialNumber.index"),
        startDate,
        endDate
      );
      gridRef.current.api.exportDataAsCsv(params);
      // exportAsExcel(formattedItem, t("inventory.notActiveSerialNumber.index"));
      enqueueSnackbar("นำออกรายงานสำเร็จ", {
        variant: "success",
      });
    } catch (err) {
      enqueueSnackbar("นำออกรายงานไม่สำเร็จ", {
        variant: "error",
      });
    } finally {
      setIsLoadingExport(false);
    }
  };

  const getAllItems = useCallback(
    async (isDefault) => {
      setIsLoadingFilter(true);
      let input = {
        startRow: 0,
        endRow: 999999,
        filterModel: {
          is_active: {
            filterType: "boolean",
            type: "equals",
            filter: "false",
          },
          is_scanned: {
            filterType: "boolean",
            type: "equals",
            filter: "false",
          },
          created_date: {
            filter: dateToUnix(
              moment(new Date()).startOf("day").subtract(6, "day")
            ),
            filterTo: dateToUnix(moment(new Date()).endOf("day")),
            filterType: "number",
            type: "inRange",
          },
        },
        sortModel: [{ colId: "created_date", sort: "asc" }],
      };
      if (isDefault) {
        const { results } = await InventoryService.getAllTEAggrid(input);
        setAllItems(results);
      } else {
        const getDateValue = getValues("date");
        const getDateToValue = getValues("dateTo");
        input.filterModel.created_date.filter = dateToUnix(
          moment(getDateValue).startOf("day")
        );
        input.filterModel.created_date.filterTo = dateToUnix(getDateToValue);
        const { results } = await InventoryService.getAllTEAggrid(input);
        setAllItems(results);
      }
      setIsLoadingFilter(false);
    },
    [getValues]
  );

  const onFilterReset = () => {
    if (gridRef) {
      gridRef.current.api.setFilterModel({});
    }
    getAllItems(true);
    reset();
  };

  useEffect(() => {
    getAllItems(true);
  }, [getAllItems]);

  return (
    <>
      <CustomizedBreadcrumbs breadcrumbs={breadcrumbs} />
      <Box sx={{ my: 3, display: "flex", justifyContent: "space-between" }}>
        <Typography variant="h5">
          {t("inventory.notActiveSerialNumber.index")}
        </Typography>
        <CustomizedButton
          sx={{ mr: 2 }}
          title={t("inventory.exportReport")}
          variant="contained"
          onClick={exportHandler}
          disabled={isLoadingExport}
        />
      </Box>
      <Box mb={2}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={12} md={6}>
            <NewReportDateFilter
              t={t}
              control={control}
              setValue={setValue}
              getValues={getValues}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={2.5} alignSelf="center">
            <Box display="flex" gap={2}>
              <CustomizedButton
                title={t("button.submitFilter")}
                variant="contained"
                onClick={() => getAllItems(false)}
                disabled={isLoadingFilter}
                fullWidth
              />
              <CustomizedButton
                title={t("button.resetFilter")}
                variant="outlined"
                onClick={onFilterReset}
                fullWidth
              />
            </Box>
          </Grid>
        </Grid>
      </Box>
      <AgGrid
        ref={gridRef}
        columnDefs={columnDefs}
        rowData={formatValueForAgGrid()}
        height={649}
      />
    </>
  );
};

export default NotActiveSerialNumber;
